/**
 * پیگیری رنگرزی (v2.168.0) — پنلِ گوشی‌محورِ پارچهٔ خام ⇐ رنگرزی ⇐ رنگ‌ها.
 *
 * کاربر (۱۴۰۵/۰۸/۱۰): «پارچهٔ خام در تاریخ‌های مشخص وارد رنگرزی می‌شود: نخ، تعداد طاقه، دهانه، کد رنگرزی، عرضِ
 * درخواستی، کدِ رهگیریِ رنگرزی؛ پارت‌ها ممکن است با هم ادغام شوند یا مانده به پارتِ بعدی برود؛ آخرش موجودیِ خامِ
 * بی‌سفارش، در رنگرزی و تحویل‌شده را ببینیم؛ سفارش PDF شود که بفرستد. گوشی‌اش خیلی کوچک است.» واحد فقط طاقه.
 *
 * داده یک‌بار (`/board` + `/options`) خوانده می‌شود و همهٔ تب‌ها از همان ساخته می‌شوند؛ هر ذخیره ⇒ دوباره‌خوانی.
 * پارت/سفارشِ باز در آدرس (`?lot=` / `?order=`) می‌نشیند تا دکمهٔ «برگشت»ِ گوشی پنجره را ببندد.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Empty, Input, Segmented, Spin, Tag, Tooltip, message } from "antd";
import { PlusOutlined, ReloadOutlined, SearchOutlined, SettingOutlined } from "@ant-design/icons";
import api from "../../api/client";
import { apiError } from "../../utils/apiError";
import useUrlTab from "../../utils/useUrlTab";
import { useAuth } from "../../context/AuthContext";
import DyeLotSheet from "./DyeLotSheet";
import DyeOrderSheet from "./DyeOrderSheet";
import DyeSettings from "./DyeSettings";
import { BASE, DELIVERY, STAGES, STATUS, fa, lbl, n0, stageOf, toEn } from "./dyeShared";
import DyeOpening from "./DyeOpening";
import { loadOrder, saveOrder } from "../../utils/layoutStore";
import "./dye.css";

const TABS = [
  { value: "lots", label: "پارت‌ها" },
  { value: "stock", label: "مانده" },
  { value: "orders", label: "سفارش‌ها" },
  { value: "done", label: "تحویل‌شده" },
];
const EMPTY_BOARD = { lots: [], orders: [], transfers: [] };

/** انتخابی که per کاربر یادش می‌ماند (`layoutStore` ⇒ روی سرور هم همگام می‌شود). */
function useRemembered(key, def) {
  const [v, set] = useState(() => loadOrder("dye", key) || def);
  return [v, (x) => { saveOrder("dye", key, x); set(x); }];
}

function LotCard({ l, lotsById, onOpen }) {
  return (
    <div className="dye-card" onClick={() => onOpen(l.id)}>
      <div className="dye-card-h">
        <span className="dye-label">{lbl(l.label)}</span>
        <span className="dye-mute">{fa(l.entry_date_fa)}</span>
        <span className="dye-grow" />
        <span className={"dye-rem" + (l.remaining < 0 ? " dye-neg" : l.remaining > 0 ? " dye-pos" : "")}>
          مانده {fa(l.remaining)}</span>
      </div>
      <div className="dye-card-b">
        {l.is_opening && <Tag color="gold">اولِ دوره</Tag>}
        <span><b>{fa(l.tagheh)}</b> طاقه</span>
        {l.yarn && <span>نخ {fa(l.yarn)}</span>}
        {l.opening && <span>دهانه {fa(l.opening)}</span>}
        {l.width && <span>عرض {fa(l.width)}</span>}
      </div>
      {l.delivery !== "none" && (
        <div className="dye-card-f">
          <Tag color={DELIVERY[l.delivery].color}>{DELIVERY[l.delivery].label}</Tag>
          <span>سفارش {fa(l.ordered)} · تحویل {fa(l.delivered)}</span>
        </div>
      )}
      {(l.track || l.dyehouse || l.merged_with.length > 0) && (
        <div className="dye-card-f">
          {l.dyehouse && <span>{l.dyehouse}</span>}
          {l.track && <span>رهگیری {fa(l.track)}</span>}
          {l.merged_with.length > 0 && (() => {
            const g = [l, ...l.merged_with.map((id) => lotsById[id]).filter(Boolean)];
            const sum = (k) => g.reduce((x, y) => x + n0(y[k]), 0);
            return <Tag color="purple">ادغام با {l.merged_with.map((id) => lbl(lotsById[id]?.label)).join("، ")}
              {` · گروه: ورود ${fa(sum("tagheh") + sum("transfer_in"))}، سفارش ${fa(sum("ordered"))}، مانده ${fa(sum("remaining"))}`}</Tag>;
          })()}
          {l.transfer_out > 0 && <Tag>{fa(l.transfer_out)} منتقل شد</Tag>}
          {l.transfer_in > 0 && <Tag>{fa(l.transfer_in)} انتقالی</Tag>}
        </div>
      )}
    </div>
  );
}

function OrderCard({ o, onOpen }) {
  return (
    <div className="dye-card" onClick={() => onOpen(o.id)}>
      <div className="dye-card-h">
        <span className="dye-label">سفارش {lbl(o.code)}</span>
        <span className="dye-mute">{fa(o.order_date_fa)}</span>
        <span className="dye-grow" />
        <Tag color={STATUS[o.status].color}>{STATUS[o.status].label}</Tag>
      </div>
      <div className="dye-card-b">
        <span>پارت {o.lots.map((x) => lbl(x.label)).join(" + ")}</span>
        <span><b>{fa(o.total)}</b> طاقه</span>
        {o.delivered > 0 && o.status !== "delivered" && <span>تحویل {fa(o.delivered)}</span>}
      </div>
      <div className="dye-card-f">
        {o.colors.map((c) => <span key={c.id}>{c.color_name} {fa(c.tagheh)}</span>)}
        {o.dyehouse && <span className="dye-mute">· {o.dyehouse}</span>}
      </div>
    </div>
  );
}

export default function DyePage() {
  const { can } = useAuth();
  const canEdit = can("dye.edit");
  const canDelete = can("dye.delete");
  // تب، فیلترها و بخشِ تنظیمات per کاربر یادش می‌ماند (کاربر v2.170: «اگر منوی دیگری انتخاب کردیم یادش بماند»)
  const [tab, setTabUrl] = useUrlTab("tab", loadOrder("dye", "tab") || "lots", [...TABS.map((t) => t.value), "settings"]);
  const setTab = (v) => { saveOrder("dye", "tab", v); setTabUrl(v); };
  const [sp, setSp] = useSearchParams();
  const pushed = useRef(false);
  const [board, setBoard] = useState(EMPTY_BOARD);
  const [opts, setOpts] = useState({ kinds: {}, options: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [lotFilter, setLotFilter] = useRemembered("lots-filter", "all");
  const [orderFilter, setOrderFilter] = useRemembered("orders-filter", "all");
  const [setPane, setSetPane] = useRemembered("settings-pane", "opening");
  const [newOrderLots, setNewOrderLots] = useState([]);

  const load = useCallback(async () => {
    try {
      const [b, o] = await Promise.all([api.get(`${BASE}/board`), api.get(`${BASE}/options`)]);
      setBoard(b.data); setOpts(o.data);
    } catch (e) { message.error(apiError(e, "خواندنِ اطلاعاتِ رنگرزی ناموفق بود")); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const lotsById = useMemo(() => Object.fromEntries(board.lots.map((l) => [l.id, l])), [board]);
  const lotParam = sp.get("lot");
  const orderParam = sp.get("order");

  const openParam = (key, val, replace = false) => {
    if (!replace) pushed.current = true;
    setSp((prev) => { const n = new URLSearchParams(prev); n.delete("lot"); n.delete("order"); n.set(key, String(val)); return n; },
      { replace });
  };
  const closeSheet = () => {
    if (pushed.current) { pushed.current = false; window.history.back(); return; }
    setSp((prev) => { const n = new URLSearchParams(prev); n.delete("lot"); n.delete("order"); return n; }, { replace: true });
  };
  const openLot = (id) => openParam("lot", id);
  const openOrder = (id) => openParam("order", id);
  const newOrder = (lotIds) => { setNewOrderLots(lotIds || []); openParam("order", "new"); };

  // ── خلاصه‌ها ──
  const rawLeft = board.lots.reduce((s, l) => s + Math.max(l.remaining, 0), 0);
  const pending = useMemo(() => board.orders.flatMap((o) => o.colors
    .filter((c) => c.tagheh > c.delivered_tagheh).map((c) => ({ ...c, order: o }))), [board]);
  const inDye = pending.reduce((s, c) => s + c.tagheh - c.delivered_tagheh, 0);
  const byYarn = useMemo(() => {
    const g = {};
    const get = (y) => (g[y || "—"] = g[y || "—"] || { yarn: y || "—", raw: 0, inDye: 0, lots: [] });
    board.lots.filter((l) => l.remaining > 0).forEach((l) => { const x = get(l.yarn); x.raw += l.remaining; x.lots.push(l); });
    pending.forEach((c) => {
      const y = lotsById[c.order.lots[0]?.lot_id]?.yarn;
      get(y).inDye += c.tagheh - c.delivered_tagheh;
    });
    return Object.values(g).filter((x) => x.raw > 0 || x.inDye > 0).sort((a, b) => b.raw - a.raw);
  }, [board, pending, lotsById]);
  const deliveredTotal = board.orders.reduce((s, o) => s + o.delivered, 0);

  const qq = toEn(q).trim();
  const match = (l) => !qq || [l.label, l.yarn, l.track, l.dyehouse, l.entry_date_fa, l.opening]
    .some((x) => toEn(x || "").includes(qq));
  // اولِ دوره پارت نیست ⇒ در فهرستِ پارت‌ها نمی‌آید. چیدمان به ترتیبِ مرحله: خام ⇐ در رنگرزی ⇐ تحویل‌شده
  const lotsShown = board.lots.filter((l) => !l.is_opening && (lotFilter === "all" || stageOf(l) === lotFilter) && match(l));
  const lotGroups = STAGES.map((st) => ({ ...st, lots: lotsShown.filter((l) => stageOf(l) === st.value) }))
    .filter((g) => g.lots.length);
  const ordersShown = board.orders.filter((o) => (orderFilter === "all"
      || (orderFilter === "done" ? o.status === "delivered" : o.status !== "delivered"))
    && (!qq || [o.code, o.dyehouse, o.driver, ...o.lots.map((x) => x.label), ...o.colors.map((c) => c.color_name)]
      .some((x) => toEn(x || "").includes(qq))));
  const doneGroups = useMemo(() => {
    const g = {};
    board.orders.forEach((o) => o.colors.filter((c) => c.delivered_tagheh > 0).forEach((c) => {
      const k = `${c.color_name}|${c.color_code || ""}`;
      (g[k] = g[k] || { name: c.color_name, code: c.color_code, total: 0, rows: [] });
      g[k].total += c.delivered_tagheh;
      g[k].rows.push({ ...c, order: o });
    }));
    return Object.values(g).sort((a, b) => b.total - a.total);
  }, [board]);

  const options = opts.options || {};
  const lotOpen = !!lotParam;
  const orderOpen = !!orderParam;

  return (
    <div className="dye-page">
      <div className="dye-head">
        <h2>پیگیری رنگرزی</h2>
        <span className="dye-grow" />
        <Tooltip title="تازه‌سازی"><Button icon={<ReloadOutlined />} onClick={() => { setLoading(true); load(); }} /></Tooltip>
        <Tooltip title="گزینه‌های پیش‌فرض">
          <Button icon={<SettingOutlined />} type={tab === "settings" ? "primary" : "default"}
            onClick={() => setTab(tab === "settings" ? "lots" : "settings")} /></Tooltip>
      </div>

      <div className="dye-stats">
        <div onClick={() => setTab("stock")}><b>{fa(rawLeft)}</b><span>خامِ بی‌سفارش</span></div>
        <div onClick={() => setTab("stock")}><b>{fa(inDye)}</b><span>در رنگرزی</span></div>
        <div onClick={() => setTab("done")}><b>{fa(deliveredTotal)}</b><span>تحویل‌شده</span></div>
      </div>

      {canEdit && tab !== "settings" && (
        <div className="dye-new">
          <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => openParam("lot", "new")}>پارتِ تازه</Button>
          <Button size="large" icon={<PlusOutlined />} onClick={() => newOrder([])}>سفارشِ رنگ</Button>
        </div>
      )}

      {tab !== "settings" && (
        <div className="dye-tabs gx-sticky-bar">
          <Segmented block value={tab} onChange={setTab} options={TABS} />
        </div>
      )}

      {loading ? <div className="dye-center"><Spin /></div> : (
        <>
          {tab === "lots" && (
            <>
              <div className="dye-filter">
                <Input allowClear prefix={<SearchOutlined />} placeholder="کد، نخ، تاریخ…" value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
              <div className="dye-scroll-x" style={{ marginBottom: 8 }}>
                <Segmented value={lotFilter} onChange={setLotFilter}
                  options={[{ value: "all", label: "همه" }, ...STAGES]} />
              </div>
              {lotsShown.length === 0 && <Empty description={board.lots.length ? "پارتی با این فیلتر نیست" : "هنوز پارتی ثبت نشده"} />}
              {lotGroups.map((g) => (
                <React.Fragment key={g.value}>
                  {lotFilter === "all" && <div className="dye-sec">{g.label} <span className="dye-mute">({fa(g.lots.length)})</span></div>}
                  {g.lots.map((l) => <LotCard key={l.id} l={l} lotsById={lotsById} onOpen={openLot} />)}
                </React.Fragment>
              ))}
            </>
          )}

          {tab === "stock" && (
            <>
              {/* هر نخ جدا رنگ می‌شود ⇒ مانده به تفکیکِ نخ (کاربر: «بدانیم از نخِ ۳۰ یا ۴۰ لاکره هرکدام چقدر داریم») */}
              <div className="dye-sec">خامِ بی‌سفارش — {fa(rawLeft)} طاقه</div>
              {byYarn.length === 0 && <div className="dye-mute">ماندهٔ خامی نیست.</div>}
              {byYarn.map((g) => (
                <div className="dye-card" key={g.yarn} style={{ cursor: "default" }}>
                  <div className="dye-card-h">
                    <span className="dye-label">نخ {fa(g.yarn)}</span><span className="dye-grow" />
                    <span className="dye-rem dye-pos">{fa(g.raw)} خام</span>
                    {g.inDye > 0 && <span className="dye-rem">{fa(g.inDye)} در رنگرزی</span>}
                  </div>
                  {g.lots.map((l) => (
                    <div className="dye-line" key={l.id}>
                      <span className="dye-label dye-click" onClick={() => openLot(l.id)}>{lbl(l.label)}</span>
                      <span className="dye-mute">{fa(l.entry_date_fa)}{l.opening ? ` · دهانه ${fa(l.opening)}` : ""}</span>
                      <span className="dye-grow" />
                      <b>{fa(l.remaining)}</b>
                      {canEdit && <Button size="small" type="primary" ghost
                        onClick={() => newOrder([l.id, ...l.merged_with.filter((id) => n0(lotsById[id]?.remaining) > 0
                          && lotsById[id]?.yarn === l.yarn)])}>سفارش</Button>}
                    </div>
                  ))}
                </div>
              ))}
              {board.lots.some((l) => l.remaining < 0) && (
                <>
                  <div className="dye-sec dye-neg">پارتِ منفی (سفارش بیشتر از ورود) — بررسی کنید</div>
                  {board.lots.filter((l) => l.remaining < 0).map((l) => (
                    <div className="dye-line" key={l.id}><span className="dye-label dye-click" onClick={() => openLot(l.id)}>{lbl(l.label)}</span>
                      <span className="dye-grow" /><b className="dye-neg">{fa(l.remaining)}</b></div>
                  ))}
                </>
              )}
              <div className="dye-sec">در رنگرزی — {fa(inDye)} طاقه</div>
              {pending.length === 0 && <div className="dye-mute">سفارشِ تحویل‌نشده‌ای نیست.</div>}
              {pending.map((c) => (
                <div className="dye-line dye-click" key={c.id} onClick={() => openOrder(c.order.id)}>
                  <span><b>{c.color_name}</b>{c.color_code && <span className="dye-opt-code">{fa(c.color_code)}</span>}</span>
                  <span className="dye-mute">سفارش {lbl(c.order.code)} · {c.order.lots.map((x) => lbl(x.label)).join("+")}</span>
                  <span className="dye-grow" />
                  <b>{fa(c.tagheh - c.delivered_tagheh)}</b>
                </div>
              ))}
            </>
          )}

          {tab === "orders" && (
            <>
              <div className="dye-filter">
                <Input allowClear prefix={<SearchOutlined />} placeholder="شماره، رنگ، پارت…" value={q} onChange={(e) => setQ(e.target.value)} />
                <Segmented value={orderFilter} onChange={setOrderFilter}
                  options={[{ value: "all", label: "همه" }, { value: "open", label: "در رنگرزی" }, { value: "done", label: "تحویل‌شده" }]} />
              </div>
              {ordersShown.length === 0 && <Empty description="سفارشی نیست" />}
              {/* به ترتیبِ مرحله: در رنگرزی ⇐ تحویلِ ناقص ⇐ تحویل‌شده */}
              {["ordered", "partial", "delivered"].map((st) => ordersShown.filter((o) => o.status === st).map((o) => (
                <OrderCard key={o.id} o={o} onOpen={openOrder} />)))}
            </>
          )}

          {tab === "done" && (
            <>
              {doneGroups.length === 0 && <Empty description="هنوز تحویلی ثبت نشده" />}
              {doneGroups.map((g) => (
                <div className="dye-card" key={`${g.name}|${g.code}`}>
                  <div className="dye-card-h">
                    <span className="dye-label">{g.name}</span>
                    {g.code && <span className="dye-opt-code">{fa(g.code)}</span>}
                    <span className="dye-grow" /><b>{fa(g.total)} طاقه</b>
                  </div>
                  {g.rows.map((r) => (
                    <div className="dye-line dye-click" key={r.id} onClick={() => openOrder(r.order.id)}>
                      <span>سفارش {lbl(r.order.code)}</span>
                      <span className="dye-mute">پارت {r.order.lots.map((x) => {
                        const l = lotsById[x.lot_id];
                        return lbl(x.label) + (l?.track ? ` (رهگیری ${fa(l.track)})` : "");
                      }).join(" + ")}</span>
                      <span className="dye-grow" /><b>{fa(r.delivered_tagheh)}</b>
                      <span className="dye-mute">{fa(r.delivered_date_fa)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}

          {tab === "settings" && (
            <>
              <div style={{ marginBottom: 10 }}>
                <Segmented block value={setPane} onChange={setSetPane}
                  options={[{ value: "opening", label: "موجودیِ اولِ دوره" }, { value: "options", label: "گزینه‌های پیش‌فرض" }]} />
              </div>
              {setPane === "opening"
                ? <DyeOpening board={board} options={options} onSaved={load} canEdit={canEdit} canDelete={canDelete} />
                : <DyeSettings kinds={opts.kinds || {}} options={options} onChanged={load} canEdit={canEdit} />}
            </>
          )}
        </>
      )}

      <DyeLotSheet open={lotOpen} isNew={lotParam === "new"} lotId={lotParam && lotParam !== "new" ? Number(lotParam) : null}
        board={board} options={options} canEdit={canEdit} canDelete={canDelete} onClose={closeSheet}
        onSaved={async (id) => { await load(); if (id && lotParam === "new") openParam("lot", id, true); }}
        onOpenLot={(id) => openParam("lot", id)} onOpenOrder={openOrder} onNewOrder={newOrder} />
      <DyeOrderSheet open={orderOpen} orderId={orderParam && orderParam !== "new" ? Number(orderParam) : null}
        initialLots={newOrderLots} board={board} options={options} canEdit={canEdit} canDelete={canDelete}
        onClose={closeSheet} onOpenLot={openLot}
        onSaved={async (id) => { await load(); if (id && orderParam === "new") openParam("order", id, true); }} />
    </div>
  );
}
